import React from 'react';

import { Route, Routes } from 'react-router-dom';
import styled from 'styled-components';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { selectors, useAppSelector } from 'state/global-state';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { BrowsingPanel } from './browsing-panel';
import { DetailsPanel } from './details-panel';
import { DetailsPanelError } from './details-panel-error';
import { MobileBackButton } from './mobile-back-button';
import { OffersHeaderBar } from './offers-header-bar';
import { useOffersUIContext } from './offers-ui-context';

export const offerIdSlug = ':offerId';

const BrowsingPanelContainer = styled.div`
  flex: 1 1 auto;
  background-color: ${Styles.color.background};
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  ${Styles.desktop} {
    min-width: 450px;
    max-width: 550px;
    height: auto;
  }
`;

const DetailsPanelContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  height: 100%;
  overflow: auto;

  ${Styles.desktop} {
    flex: 2;
  }
`;

const DesktopDetailsRouter = styled(Routes)`
  flex: 2;
`;

export const BrowsingRoute = () => {
  const { hideHeaderBar } = useOffersUIContext();

  return (
    <BrowsingPanelContainer>
      {!hideHeaderBar && <OffersHeaderBar />}
      <BrowsingPanel />
    </BrowsingPanelContainer>
  );
};

export const DetailsRoute = () => {
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const { previewOfferId, previewOffer, showEmptyState, showMissingOffer, featureOffers } =
    useOffersUIContext();
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const { offersDetailsPanel = null, defaultBackgroundImage = null } = featureOffers || {};
  if (showEmptyState) {
    return (
      <DetailsPanelError
        backgroundImage={defaultBackgroundImage}
        image={offersDetailsPanel?.emptyStateImage ?? null}
        title={offersDetailsPanel?.emptyStateTitle ?? null}
        body={offersDetailsPanel?.emptyStateBody ?? null}
        action={offersDetailsPanel?.emptyStateAction ?? null}
        showAction
        titleTestId="details-panel-empty-state-title"
        bodyTestId="details-panel-empty-state-body"
        actionButtonTestId="details-panel-empty-state-action-button"
      />
    );
  }

  if (showMissingOffer) {
    return (
      <DetailsPanelError
        backgroundImage={defaultBackgroundImage}
        image={offersDetailsPanel?.missingOfferImage ?? null}
        title={offersDetailsPanel?.missingOfferTitle ?? null}
        body={offersDetailsPanel?.missingOfferBody ?? null}
        action={offersDetailsPanel?.missingOfferAction ?? null}
        showAction={isMobile}
        titleTestId="details-panel-missing-offer-title"
        bodyTestId="details-panel-missing-offer-body"
        actionButtonTestId="details-panel-missing-offer-action-button"
      />
    );
  }

  return (
    <DetailsPanelContainer>
      {loyaltyOffersInCooldown && <LoyaltyOffersCooldownDrawer />}
      {previewOffer && (
        <DetailsPanel
          key={previewOfferId ?? ''}
          offer={previewOffer}
          // props for DetailsPanelError
          backgroundImage={defaultBackgroundImage}
          image={offersDetailsPanel?.missingOfferImage ?? null}
          title={offersDetailsPanel?.missingOfferTitle ?? null}
          body={offersDetailsPanel?.missingOfferBody ?? null}
          action={offersDetailsPanel?.missingOfferAction ?? null}
          showAction={isMobile}
          offersInCooldown={loyaltyOffersInCooldown}
        />
      )}
    </DetailsPanelContainer>
  );
};

export const OffersRouter: React.FC = () => {
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const { showMobileBackButton, offersUrl, offers, loading } = useOffersUIContext();
  const { trackEvent } = useMParticleContext();
  const length = offers.length;

  useEffectOnUpdates(() => {
    if (loading) {
      return;
    }

    trackEvent({
      name: CustomEventNames.OFFERS_DISPLAYED,
      type: EventTypes.Other,
      attributes: { count: length },
    });
  }, [loading, length, trackEvent]);

  return isMobile ? (
    <>
      {showMobileBackButton && <MobileBackButton to={offersUrl} />}
      <Routes>
        <Route path={''} element={<BrowsingRoute />} />
        <Route path={'/*'} element={<DetailsRoute />} />
      </Routes>
    </>
  ) : (
    <>
      <Routes>
        <Route path={''} element={<BrowsingRoute />} />
      </Routes>
      <DesktopDetailsRouter>
        <Route path={'/*'} element={<DetailsRoute />} />
      </DesktopDetailsRouter>
    </>
  );
};
